import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    Adres,
    BokkzAlgDataService,
    BokkzDivDataService, BokkzDivLogic, ContactPersoon, Dienstverlener,
    Dividendbelasting,
    DividendTaxEntity
} from '@snelstart-ventures/fiscaal/domain-logic';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { combineLatest, debounceTime, Subscription } from 'rxjs';

@Component({
    selector: 'snelstart-venture-fiscaal-contactgegevens',
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, SharedUiDetailsModule, SharedUiFormControlsModule],
    templateUrl: './contactgegevens.component.html',
    styleUrl: './contactgegevens.component.scss'
})
export class ContactgegevensComponent implements OnInit, OnDestroy {

    @Input() public id!: string;
    private combinedSubscription: Subscription | undefined;
    public divTaxEntity = {} as DividendTaxEntity;
    public aangifte = {} as Dividendbelasting;
    public contactFg = {} as FormGroup;
    public dienstverlenerFg = {} as FormGroup;
    public beconNummer: string | undefined;
    public focusedElementId: string | null = null;

    constructor(
        public algDataService: BokkzAlgDataService,
        public dataService: BokkzDivDataService,
        public divService: BokkzDivLogic,
        private fb: FormBuilder) {
        this.contactFg = this.fb.group<ContactPersoon>({
            protocolNummerNotaris: null as unknown as string,
            instellingnaam: null as unknown as string,
            beconnummer: null as unknown as string,
            bsn: null as unknown as string,
            geboortedatum: null as unknown as Date,
            voorletters: null as unknown as string,
            tussenvoegsel: null as unknown as string,
            achternaam: null as unknown as string,
            emailadres: null as unknown as string,
            telefoonnummer: null as unknown as string,
            adres: null as unknown as Adres
        });
        this.contactFg.get('telefoonnummer')?.valueChanges.pipe(
            debounceTime(750) // optioneel: wacht tot gebruiker stopt met typen
        ).subscribe(value => {
            if (value && value.includes('-')) {
                const cleaned = value.replace(/[-\s]/g, '');
                this.contactFg.patchValue({ telefoonnummer: cleaned }, { emitEvent: false });
            }
        });
        this.dienstverlenerFg = this.fb.group<Dienstverlener>({
            rsinDienstverlener: null as unknown as string,
            beconNummer: null as unknown as string,
            naamDienstverlener: null as unknown as string
        });
    }

    ngOnInit(): void {
        this.combinedSubscription = combineLatest([
            this.algDataService.intermediary,
            this.dataService.dividendTaxEntity
        ]).subscribe(([intermediary, divTaxEntity]) => {
            if (intermediary) {
                this.beconNummer = intermediary.beconId || undefined;
            }

            if (intermediary && intermediary.officeName && intermediary.beconId && intermediary.taxId) {
                this.dienstverlenerFg.patchValue({
                    beconNummer: intermediary.beconId,
                    naamDienstverlener: intermediary.officeName,
                    rsinDienstverlener: intermediary.taxId
                });
            }

            if (divTaxEntity) {
                const aangifte = divTaxEntity.aangifte;
                this.divTaxEntity = divTaxEntity;
                if (aangifte) {
                    this.aangifte = aangifte;
                    this.contactFg.patchValue(aangifte.contactPersoon, { emitEvent: false });
                    if (aangifte.dienstverlener && (!intermediary.beconId || !intermediary.officeName || !intermediary.taxId)) {
                        this.dienstverlenerFg.patchValue(aangifte.dienstverlener, { emitEvent: false });
                    }
                }
            }
        });
    }

    ngOnDestroy(): void {
        if (this.combinedSubscription) {
            this.combinedSubscription.unsubscribe();
        }
    }

    saveForm(): void {
        this.contactFg.updateValueAndValidity();
        const telefoonnummer = this.contactFg.value.telefoonnummer?.replace(/[-\s]/g, '') || null;
        this.divTaxEntity.aangifte = {
            ...this.aangifte,
            contactPersoon: {
                ...this.aangifte.contactPersoon,
                protocolNummerNotaris: this.contactFg.value.protocolNummerNotaris,
                instellingnaam: this.contactFg.value.instellingnaam,
                beconnummer: this.contactFg.value.beconnummer,
                bsn: this.contactFg.value.bsn,
                geboortedatum: this.contactFg.value.geboortedatum,
                voorletters: this.contactFg.value.voorletters,
                tussenvoegsel: this.contactFg.value.tussenvoegsel,
                achternaam: this.contactFg.value.achternaam,
                emailadres: this.contactFg.value.emailadres,
                telefoonnummer: telefoonnummer,
                adres: this.contactFg.value.adres
            },
            dienstverlener: {
                beconNummer: this.dienstverlenerFg.getRawValue().beconNummer,
                naamDienstverlener: this.dienstverlenerFg.getRawValue().naamDienstverlener,
                rsinDienstverlener: this.dienstverlenerFg.getRawValue().rsinDienstverlener
            }
        } as Dividendbelasting;
        this.dataService.updateDividendTaxEntity(this.divTaxEntity);
        this.divService.getCalculation(this.divTaxEntity);
    }
}
